import React, { useState, useMemo, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQueryClient } from 'react-query'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Progress } from '../../components/ui/progress'
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar'
import { CoachLogic } from '../../components/ui/coach-logic'
import { FeedbackDialog } from '../../components/ui/feedback-dialog'
import { ErrorBoundary } from '../../components/ui/error-boundary'
import type { FeedbackType, PlayContext } from '../../components/ui/insight-feedback'
import { getRoleFromPath } from '../../utils/helper'
import {
  ArrowLeft,
  Zap,
  TrendingUp,
  RefreshCw,
  Target,
  X,
  Check,
  AlertCircle,
  Loader2,
  Clock,
} from 'lucide-react'
import {
  useGetInsightQuery,
  useUpdateUserTaskMutation,
  useUpdateInsightMutation,
  useCreateFlowChatSessionMutation,
  UserTaskItemStatus,
  UserTaskSuggestedItemStatus,
  OnboardingFlowType,
  ChatSessionStatus,
  useGetChatSessionsQuery,
  useGetBusinessGoalsQuery,
  useUpdateBusinessGoalMutation,
  UserTaskSource,
} from '../../__generated__/api-types-and-hooks'
import { fetcher } from '../../lib/api-client'
import { AppAIChatPopup } from '../../components/AppOnboarding/AppRooms'
import CoachLogicAvatar from '../../appAssets/appImages/Avatar.png'
import { AppAvatar } from '../../components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from '../../components/Chat/helper'
import { AppModalBox } from '../../components/Common/AppModalBox/AppModalBox.component'
import { AppButton } from '../../components/Common/AppButton/AppButton.component'
import { useGetBusinessProfile } from '../../appHooks/useGetBusinessProfile'
import { AppTask } from '../../components/AppKanbanBoard/AppTask/AppTask.component'
import AppCrossIcon from '../../components/Common/AppSvgIcons/AppModalCrossIcon'
import { categoryType } from '../../appConfig/data'

// Helper function to generate consistent avatar colors based on user ID or name
const getAvatarColor = (identifier: string) => {
  const colors = [
    { bg: '#FEF3C7', text: '#92400E' }, // Amber/Yellow
    { bg: '#DBEAFE', text: '#1E40AF' }, // Blue
    { bg: '#FCE7F3', text: '#9F1239' }, // Pink
    { bg: '#D1FAE5', text: '#065F46' }, // Green
    { bg: '#E0E7FF', text: '#3730A3' }, // Indigo
    { bg: '#FED7AA', text: '#9A3412' }, // Orange
    { bg: '#E9D5FF', text: '#6B21A8' }, // Purple
    { bg: '#FBCFE8', text: '#831843' }, // Rose
  ]

  // Generate a consistent index based on the identifier
  let hash = 0
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % colors.length
  return colors[index]
}

// Types
interface Play {
  id: string
  title: string
  timeEstimate: string
  impact: 'high' | 'medium' | 'low'
  priority: 'high' | 'medium' | 'low'
  category: 'do-more' | 'change' | 'start'
  description: string
  whyItMatters: string
  steps: string[]
  suggestedStatus?: string
  assigneeId?: string
  userId?: string
  createdById?: string
  assigneeName?: string
  assigneeFirstName?: string
  assigneeLastName?: string
  hasBeenViewed?: boolean
}

interface Goal {
  id: string
  title: string
  deadline: string
  progress: number
  priority: 'high' | 'medium' | 'low'
  description: string
  whyItMatters: string
  metrics: string[]
  target: string
  status?: UserTaskItemStatus
  suggestedStatus?: UserTaskSuggestedItemStatus
}

interface Insight {
  id: string
  title: string
  description: string
  category: string
  priority: 'high' | 'medium' | 'low'
  why: string
  actionableDirectives: {
    doMore: string[]
    changeStop: string[]
    start: string[]
  }
  metrics: Array<{
    name: string
    value: string
    trend: 'up' | 'down'
    benchmark: string
  }>
  plays: Play[]
  goals: Goal[]
}

const InsightDetail = () => {
  const { tenantId, insightId, clientId } = useParams<{
    tenantId: string
    insightId: string
    clientId?: string
  }>()
  const currentUserId = useSelector((state: any) => state.user.user.id)
  // Use clientId if viewing client insights, otherwise use current user's ID
  const userId = clientId || currentUserId
  const queryClient = useQueryClient()
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('insight')
  const [blockingItemTitle, setBlockingItemTitle] = useState('')
  const [blockingPlayId, setBlockingPlayId] = useState<string | null>(null)
  const [blockingPlayContext, setBlockingPlayContext] = useState<PlayContext | null>(null)
  const [playSessionId, setPlaySessionId] = useState<string | null>(null)
  const [playsData, setPlaysData] = useState<any[]>([])
  const [playsLoading, setPlaysLoading] = useState(false)
  const [goalsData, setGoalsData] = useState<any[]>([])
  const [acceptGoalSessionId, setAcceptGoalSessionId] = useState<string | null>(null)
  const [isAcceptGoalChatOpen, setIsAcceptGoalChatOpen] = useState(false)
  const [loadingGoalId, setLoadingGoalId] = useState<string | null>(null)
  const [rejectingGoalId, setRejectingGoalId] = useState<string | null>(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null)
  const [pendingGoalId, setPendingGoalId] = useState<string | null>(null)
  const [showPlayEditModal, setShowPlayEditModal] = useState(false)
  const [selectedPlayForEdit, setSelectedPlayForEdit] = useState<any | null>(null)

  // Determine the correct back link based on whether viewing client insights
  const backToInsightsLink = clientId
    ? `/${getRoleFromPath()}/${tenantId}/clients/${clientId}/insights`
    : `/${getRoleFromPath()}/${tenantId}/insights`

  // Handle session creation from feedback dialog
  const handleFeedbackSessionCreated = async (sessionId: string) => {
    if (feedbackType === 'play' && blockingPlayId && tenantId && userId) {
      // Save session to the play (UserTask)
      try {
        updateUserTask({
          input: {
            taskId: blockingPlayId,
            tenantId,
            userId,
            session: sessionId,
          },
        })
        setPlaySessionId(sessionId)
      } catch (error) {
        console.error('Failed to update play feedback session:', error)
      }
    } else if (feedbackType === 'insight' && insightId && tenantId && userId) {
      // Save session to the insight
      try {
        await updateInsight({
          input: {
            tenantId,
            userId,
            insightId,
            session: sessionId,
          },
        })
      } catch (error) {
        console.error('Failed to update insight feedback session:', error)
      }
    }
  }

  // Add mutation for updating user task
  const { mutate: updateUserTask } = useUpdateUserTaskMutation()
  const { mutateAsync: updateInsight } = useUpdateInsightMutation()
  const { mutate: createFlowChatSession } = useCreateFlowChatSessionMutation()
  const { mutate: updateBusinessGoal } = useUpdateBusinessGoalMutation()
  const user = useSelector((state: any) => state.user.user)

  // Fetch assignee options from business profile to get user/team member information
  const { assigneeOptions, logo, advisorsData } = useGetBusinessProfile({
    enabled: true,
    paramUserId: clientId || currentUserId,
  })

  // Fetch insight data from API
  const {
    data: insightResponse,
    isLoading,
    isError,
  } = useGetInsightQuery(
    {
      tenantId: tenantId!,
      userId,
      insightId: insightId!,
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!tenantId && !!insightId,
    }
  )

  // Memoize goalIds and playIds to avoid changing on every render
  const goalIds = useMemo(() => insightResponse?.getInsight?.goalsId || [], [insightResponse])
  const playIds = useMemo(() => insightResponse?.getInsight?.playsId || [], [insightResponse])

  // Mark insight as viewed when the component mounts
  // Only mark as viewed if this is the user's own insight (not an advisor viewing a client's insight)
  useEffect(() => {
    const markAsViewed = async () => {
      // Don't mark as viewed if an advisor/admin is viewing a client's insight
      if (clientId) {
        return
      }

      if (insightId && tenantId && userId && insightResponse?.getInsight) {
        try {
          await updateInsight({
            input: {
              tenantId,
              userId,
              insightId,
              hasBeenViewed: true,
            },
          })
          // Invalidate the insights counter for the current user's own insights
          queryClient.invalidateQueries(['GetInsights', { tenantId, userId, hasBeenViewed: false }])
        } catch (error) {
          console.error('Failed to mark insight as viewed:', error)
        }
      }
    }
    markAsViewed()
  }, [insightId, tenantId, userId, insightResponse, updateInsight, queryClient, clientId])

  const { data: chatSessionsData } = useGetChatSessionsQuery({
    input: {
      userId,
    },
  })

  // Fetch goals using the generated hook
  const {
    data: goalsQueryData,
    isLoading: goalsLoading,
    refetch: refetchGoals,
  } = useGetBusinessGoalsQuery(
    {
      input: {
        tenantId: tenantId!,
        userId,
      },
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!tenantId && !!userId && goalIds.length > 0,
    }
  )

  // Filter and set goals data based on goalIds
  useEffect(() => {
    if (goalsQueryData?.getBusinessGoals && goalIds.length > 0) {
      const allGoals = goalsQueryData.getBusinessGoals || []
      const matchingGoals = allGoals.filter((goal: any) => goalIds.includes(goal.goalId))
      setGoalsData(matchingGoals)
    } else if (goalIds.length === 0) {
      setGoalsData([])
    }
  }, [goalsQueryData, goalIds])

  const fetchGoalsList = async () => {
    await refetchGoals()
  }

  // Fetch individual plays using getUserTask API
  useEffect(() => {
    const fetchPlays = async () => {
      if (!tenantId || playIds.length === 0) {
        setPlaysData([])
        return
      }

      setPlaysLoading(true)
      try {
        const query = `
          query GetUserTask($input: GetUserTaskInput!) {
            getUserTask(input: $input) {
              taskId
              name
              timeline
              taskPriority
              category
              description
              why
              playType
              suggestedStatus
              session
              assigneeId
              userId
              createdById
              createdBy
              status
              startDate
              expectedEndDate
              goalId
              estimatedEffortInMinutes
              instructionForTask
              hasBeenViewed
            }
          }
        `

        const fetchPromises = playIds.map(async (playId) => {
          try {
            const response: any = await fetcher(query, {
              input: { tenantId, taskId: playId, userId },
            })()
            return response.getUserTask
          } catch (error: any) {
            console.error(`Error fetching play ${playId}:`, error)
            return null
          }
        })

        const results = await Promise.all(fetchPromises)
        setPlaysData(results.filter(Boolean))
      } catch (error) {
        console.error('Error fetching plays:', error)
        setPlaysData([])
      } finally {
        setPlaysLoading(false)
      }
    }

    fetchPlays()
  }, [tenantId, userId, playIds])

  // Function to refetch plays data
  const refetchPlays = async () => {
    if (!tenantId || playIds.length === 0) {
      return
    }

    setPlaysLoading(true)
    try {
      const query = `
        query GetUserTask($input: GetUserTaskInput!) {
          getUserTask(input: $input) {
            taskId
            name
            timeline
            taskPriority
            category
            description
            why
            playType
            suggestedStatus
            session
            assigneeId
            userId
            createdById
            createdBy
            status
            startDate
            expectedEndDate
            goalId
            estimatedEffortInMinutes
            instructionForTask
            hasBeenViewed
          }
        }
      `

      const fetchPromises = playIds.map(async (playId) => {
        try {
          const response: any = await fetcher(query, {
            input: { tenantId, taskId: playId, userId },
          })()
          return response.getUserTask
        } catch (error: any) {
          console.error(`Error refetching play ${playId}:`, error)
          return null
        }
      })

      const results = await Promise.all(fetchPromises)
      setPlaysData(results.filter(Boolean))
    } catch (error) {
      console.error('Error refetching plays:', error)
    } finally {
      setPlaysLoading(false)
    }
  }

  // Transform API response to component data structure
  const insightData: Insight | null = insightResponse?.getInsight
    ? (() => {
        // Map priority enum to lowercase string
        const priorityMap: Record<string, 'high' | 'medium' | 'low'> = {
          HIGH_PRIORITY: 'high',
          MEDIUM_PRIORITY: 'medium',
          LOW_PRIORITY: 'low',
        }
        const priority = insightResponse.getInsight.priority
          ? priorityMap[insightResponse.getInsight.priority] || 'medium'
          : 'medium'

        return {
          id: insightResponse.getInsight.insightId || '',
          title: insightResponse.getInsight.name || '',
          description: insightResponse.getInsight.description || '',
          category: insightResponse.getInsight.category || 'revenue',
          priority,
          why: insightResponse.getInsight.why || '',
          actionableDirectives: {
            doMore: [],
            changeStop: [],
            start: [],
          },
          metrics:
            insightResponse.getInsight.metrics?.map((m) => ({
              name: m?.metricTitle || '',
              value: m?.current_status?.toString() || '',
              trend: (m?.trend?.toLowerCase() as 'up' | 'down') || 'up',
              benchmark: m?.benchmark?.toString() || '',
            })) || [],
          plays: [],
          goals: [],
        }
      })()
    : null

  // Transform fetched goals data
  const goals = useMemo(() => {
    // Helper function to map priority enum to lowercase
    const mapGoalPriority = (priority: string | null | undefined): 'high' | 'medium' | 'low' => {
      if (!priority) return 'medium'
      switch (priority) {
        case 'HIGH_PRIORITY':
          return 'high'
        case 'MEDIUM_PRIORITY':
          return 'medium'
        case 'LOW_PRIORITY':
          return 'low'
        default:
          return 'medium'
      }
    }

    return goalIds.map((goalId, index) => {
      // Find the corresponding goal data from fetched results
      const goalData = goalsData.find((g) => g?.goalId === goalId)

      if (!goalData) {
        return {
          id: goalId,
          title: goalsLoading ? 'Loading...' : 'NULL',
          deadline: 'NULL',
          progress: 0,
          priority: 'medium' as const,
          categoryDisplay: 'NULL',
          description: goalsLoading ? 'Loading goal details...' : 'NULL',
          whyItMatters: 'NULL',
          metrics: [],
          target: 'NULL',
          status: UserTaskItemStatus.Pending,
          suggestedStatus: UserTaskSuggestedItemStatus.Pending,
        }
      }

      return {
        id: goalData.goalId || goalId,
        title: goalData.name || 'NULL',
        deadline: goalData.expectedEndDate || 'NULL',
        progress: goalData.progress || 0,
        priority: mapGoalPriority(goalData.goalPriority),
        categoryDisplay: goalData.category || 'NULL',
        description: goalData.description || 'NULL',
        whyItMatters: goalData.why || 'NULL',
        metrics: [],
        target: 'NULL', // Target field doesn't exist in BusinessGoal schema
        status: goalData.status,
        suggestedStatus: goalData.suggestedStatus,
      }
    })
  }, [goalsData, goalIds, goalsLoading])

  // Transform fetched plays data
  const plays = useMemo(() => {
    // Helper function to map PlayType to category
    const mapPlayTypeToCategory = (
      playType: string | null | undefined
    ): 'do-more' | 'change' | 'start' => {
      if (!playType) return 'start'
      switch (playType) {
        case 'DO_MORE':
          return 'do-more'
        case 'CHANGE_STOP':
          return 'change'
        case 'START':
          return 'start'
        default:
          return 'start'
      }
    }

    // Helper function to map priority enum to lowercase
    const mapPriority = (priority: string | null | undefined): 'high' | 'medium' | 'low' => {
      if (!priority) return 'medium'
      switch (priority) {
        case 'HIGH_PRIORITY':
          return 'high'
        case 'MEDIUM_PRIORITY':
          return 'medium'
        case 'LOW_PRIORITY':
          return 'low'
        default:
          return 'medium'
      }
    }

    return playIds.map((playId, index) => {
      // Find the corresponding play data from fetched results
      const playData = playsData.find((p) => p?.taskId === playId)

      if (!playData) {
        return {
          id: playId,
          title: playsLoading ? 'Loading...' : 'NULL',
          timeEstimate: 'NULL',
          impact: 'medium' as const,
          priority: 'medium' as const,
          category: 'start' as const,
          categoryDisplay: 'NULL',
          description: playsLoading ? 'Loading play details...' : 'No description available',
          whyItMatters: 'NULL',
          steps: [],
          suggestedStatus: undefined,
          assigneeId: undefined,
          userId: undefined,
          createdById: undefined,
          assigneeName: undefined,
          assigneeFirstName: undefined,
          assigneeLastName: undefined,
          hasBeenViewed: false,
        }
      }

      // Find assignee information from assigneeOptions
      const assigneeInfo = assigneeOptions?.find((option) => option.value === playData.assigneeId)

      return {
        id: playData.taskId || playId,
        title: playData.name || 'NULL',
        timeEstimate: playData.timeline || 'NULL',
        impact: 'medium' as const, // Not available in UserTask schema
        priority: mapPriority(playData.taskPriority),
        category: mapPlayTypeToCategory(playData.playType),
        categoryDisplay: playData.category || 'NULL',
        description: playData.description || 'No description available',
        whyItMatters: playData.why || 'NULL',
        steps: [],
        suggestedStatus: playData.suggestedStatus,
        assigneeId: playData.assigneeId,
        userId: playData.userId,
        createdById: playData.createdById,
        assigneeName: assigneeInfo?.label,
        assigneeFirstName: assigneeInfo?.user?.firstName,
        assigneeLastName: assigneeInfo?.user?.lastName,
        hasBeenViewed: playData.hasBeenViewed || false,
      }
    })
  }, [playsData, playIds, playsLoading, assigneeOptions])

  // Update insightData with fetched goals and plays
  const enrichedInsightData = useMemo(() => {
    if (!insightData) return null
    return {
      ...insightData,
      goals,
      plays,
    }
  }, [insightData, goals, plays])

  const handlePlayAction = async (playId: string) => {
    // Find the play data to check hasBeenViewed
    const playData = playsData.find((p) => p.taskId === playId)

    // Determine if this is the first acceptance (should open modal)
    const shouldOpenModal =
      playData &&
      playData.suggestedStatus === UserTaskSuggestedItemStatus.Pending &&
      !playData.hasBeenViewed

    // Create the updated play data
    const updatedPlayData = playData
      ? {
          ...playData,
          status: UserTaskItemStatus.Todo,
          suggestedStatus: UserTaskSuggestedItemStatus.Accepted,
          hasBeenViewed: true,
        }
      : null

    // Optimistic update - update UI immediately
    setPlaysData((prevPlays) =>
      prevPlays.map((play) =>
        play.taskId === playId
          ? {
              ...play,
              status: UserTaskItemStatus.Todo,
              suggestedStatus: UserTaskSuggestedItemStatus.Accepted,
              hasBeenViewed: true,
            }
          : play
      )
    )

    // Fire and forget - don't wait for network request
    updateUserTask(
      {
        input: {
          taskId: playId,
          tenantId: tenantId!,
          userId,
          status: UserTaskItemStatus.Todo,
          suggestedStatus: UserTaskSuggestedItemStatus.Accepted,
          hasBeenViewed: true,
        },
      },
      {
        onSuccess: () => {
          // Open modal only on first acceptance
          if (shouldOpenModal && updatedPlayData) {
            // Set the modal with the updated data immediately
            setSelectedPlayForEdit(updatedPlayData)
            setShowPlayEditModal(true)
          }
          // Don't refetch on regular accept to avoid flickering
        },
        onError: (error) => {
          console.error('Error accepting play:', error)
          // Revert optimistic update on error
          setPlaysData((prevPlays) =>
            prevPlays.map((play) =>
              play.taskId === playId
                ? {
                    ...play,
                    status: playData?.status || play.status,
                    suggestedStatus: playData?.suggestedStatus || null,
                    hasBeenViewed: playData?.hasBeenViewed || false,
                  }
                : play
            )
          )
        },
      }
    )
  }

  const handleOpenPlayEditModal = (playId: string) => {
    // Use playsData (raw data) instead of the transformed plays array
    const playData = playsData.find((p) => p.taskId === playId)
    if (playData) {
      setSelectedPlayForEdit(playData)
      setShowPlayEditModal(true)
    }
  }

  const handleGoalAction = async (goalId: string) => {
    setLoadingGoalId(goalId)

    // Check for existing session
    const existingSession = chatSessionsData?.getChatSessions?.find((session) => {
      if (
        session.flow !== OnboardingFlowType.AcceptGoal ||
        session.status === ChatSessionStatus.Completed
      ) {
        return false
      }

      try {
        const metadata = session.metadata ? JSON.parse(session.metadata) : {}
        return metadata.goalId === goalId
      } catch (e) {
        return false
      }
    })

    if (existingSession) {
      setPendingSessionId(existingSession.id)
      setPendingGoalId(goalId)
      setConfirmModalOpen(true)
      setLoadingGoalId(null)
      return
    }

    createFlowChatSession(
      {
        input: {
          flow: OnboardingFlowType.AcceptGoal,
          metadata: JSON.stringify({
            goalId,
          }),
        },
      },
      {
        onSuccess: (res) => {
          const { id } = res.createFlowChatSession
          setAcceptGoalSessionId(id)
          setIsAcceptGoalChatOpen(true)
          setLoadingGoalId(null)
        },
        onError: (error) => {
          console.error('Error creating accept goal session:', error)
          setLoadingGoalId(null)
        },
      }
    )
  }

  // Function to handle blocking a play
  const handlePlayBlock = (playId: string, playTitle: string) => {
    // Find the play data to get full context
    const playData = playsData.find((p) => p?.taskId === playId)

    // Set up play context for the feedback dialog
    const context: PlayContext = {
      playId,
      playTitle,
      playDescription: playData?.description,
      playCategory: playData?.category,
      playPriority: playData?.taskPriority,
      whyItMatters: playData?.why,
      // Include parent insight context
      insightTitle: enrichedInsightData?.title,
      insightDescription: enrichedInsightData?.description,
      insightWhy: enrichedInsightData?.why,
    }

    setBlockingPlayId(playId)
    setBlockingItemTitle(playTitle)
    setBlockingPlayContext(context)
    setFeedbackType('play')
    // Check if play already has a session
    setPlaySessionId(playData?.session || null)
    setFeedbackDialogOpen(true)
  }

  // Function to handle blocking/declining a goal
  const handleGoalBlock = async (goalId: string, goalTitle: string) => {
    setRejectingGoalId(goalId)

    // Optimistic update - update UI immediately
    setGoalsData((prevGoals) =>
      prevGoals.map((goal) =>
        goal.goalId === goalId
          ? {
              ...goal,
              status: UserTaskItemStatus.Archived,
              suggestedStatus: UserTaskSuggestedItemStatus.Rejected,
            }
          : goal
      )
    )

    // Fire and forget - don't wait for network request
    updateBusinessGoal(
      {
        input: {
          tenantId: tenantId!,
          userId,
          goalId,
          status: UserTaskItemStatus.Archived,
          suggestedStatus: UserTaskSuggestedItemStatus.Rejected,
        },
      },
      {
        onSuccess: () => {
          // Refetch goals to ensure UI is in sync with server
          refetchGoals()
          setRejectingGoalId(null)
        },
        onError: (error) => {
          console.error('Error declining goal:', error)
          // Revert optimistic update on error
          setGoalsData((prevGoals) =>
            prevGoals.map((goal) =>
              goal.goalId === goalId
                ? {
                    ...goal,
                    status: goal.status,
                    suggestedStatus: goal.suggestedStatus,
                  }
                : goal
            )
          )
          setRejectingGoalId(null)
        },
      }
    )
  }

  // Function to handle feedback dialog submission
  const handleDetailedFeedbackSubmit = (feedbackText: string) => {
    // Only block the play if blockingPlayId is set (play blocking flow)
    if (blockingPlayId) {
      const playIdToBlock = blockingPlayId
      const playData = playsData.find((p) => p.taskId === playIdToBlock)

      // Optimistic update - update UI immediately
      setPlaysData((prevPlays) =>
        prevPlays.map((play) =>
          play.taskId === playIdToBlock
            ? {
                ...play,
                status: UserTaskItemStatus.Archived,
                suggestedStatus: UserTaskSuggestedItemStatus.Rejected,
                hasBeenViewed: true,
              }
            : play
        )
      )

      // Reset blocking state immediately for better UX
      setBlockingPlayId(null)

      // Fire and forget - don't wait for network request
      updateUserTask(
        {
          input: {
            taskId: playIdToBlock,
            tenantId: tenantId!,
            userId,
            status: UserTaskItemStatus.Archived,
            suggestedStatus: UserTaskSuggestedItemStatus.Rejected,
            hasBeenViewed: true,
          },
        },
        {
          onSuccess: () => {
            // Don't refetch to avoid flickering - optimistic update is sufficient
          },
          onError: (error) => {
            console.error('Error blocking play:', error)
            // Revert optimistic update on error
            setPlaysData((prevPlays) =>
              prevPlays.map((play) =>
                play.taskId === playIdToBlock
                  ? {
                      ...play,
                      status: playData?.status || play.status,
                      suggestedStatus: playData?.suggestedStatus || null,
                      hasBeenViewed: playData?.hasBeenViewed || false,
                    }
                  : play
              )
            )
          },
        }
      )
    } else {
      console.log('Feedback received:', feedbackText)
    }
  }

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-[#FDE8E8] text-[#B91C1C] border-[#FECACA]'
      case 'medium':
        return 'bg-[#FFF7E6] text-[#D97706] border-[#FDE68A]'
      case 'low':
        return 'bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]'
      default:
        return 'bg-gray-100 text-gray-700 border-0'
    }
  }

  const getImpactBadgeClass = (impact: string) => {
    switch (impact) {
      case 'high':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'medium':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#705AF8]" />
          <p className="text-gray-600">Loading insight details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="min-h-screen bg-white p-6">
        <Link to={backToInsightsLink}>
          <Button
            variant="outline"
            size="sm"
            className="mb-6 bg-white"
            style={{ border: '1px solid #E5E7EB' }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Insights
          </Button>
        </Link>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <p className="text-gray-900 font-medium">Failed to load insight</p>
          <p className="text-gray-600 text-sm">There was an error loading this insight.</p>
        </div>
      </div>
    )
  }

  // Not found state
  if (!enrichedInsightData) {
    return (
      <div className="min-h-screen bg-white p-6">
        <Link to={backToInsightsLink}>
          <Button
            variant="outline"
            size="sm"
            className="mb-6 bg-white"
            style={{ border: '1px solid #E5E7EB' }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Insights
          </Button>
        </Link>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <AlertCircle className="h-12 w-12 text-gray-400" />
          <p className="text-gray-900 font-medium">Insight not found</p>
          <p className="text-gray-600 text-sm">The insight you're looking for doesn't exist.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Back button */}
      <div className="mb-2 px-6">
        <Link to={backToInsightsLink}>
          <Button
            variant="outline"
            size="sm"
            className="bg-white"
            style={{ border: '1px solid #E5E7EB' }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Insights
          </Button>
        </Link>
      </div>

      {/* Content Container */}
      <div className="px-6">
        {/* Coach Logic Insight Analysis */}
        {enrichedInsightData.why && (
          <CoachLogic
            message={insightResponse?.getInsight?.internal_context || 'NULL'}
            suggestions={[
              'Which play should I start with?',
              "What's the easiest win?",
              'How long will this take?',
            ]}
            className="mb-6"
          />
        )}
        {/* Insight Overview Card */}
        <Card
          className="mb-6 p-0 bg-white rounded-lg overflow-hidden"
          style={{
            border: '1px solid #E5E7EB',
            borderTop: `4px solid ${
              enrichedInsightData.priority === 'high'
                ? '#EF4444'
                : enrichedInsightData.priority === 'medium'
                ? '#FBBF24'
                : '#10B981'
            }`,
          }}
        >
          <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-[#111827] mb-2">
                  {enrichedInsightData.title}
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {enrichedInsightData.description}
                </p>
              </div>
              <div className="flex gap-2 ml-4 flex-shrink-0">
                <Badge
                  variant="outline"
                  className={`${getPriorityBadgeClass(
                    enrichedInsightData.priority
                  )} rounded-full px-3 py-1 text-xs font-medium shadow-sm`}
                >
                  {enrichedInsightData.priority.charAt(0).toUpperCase() +
                    enrichedInsightData.priority.slice(1)}
                </Badge>
              </div>
            </div>
          </div>
        </Card>{' '}
        {/* Suggested Plays Section */}
        <Card
          className="mb-6 bg-white rounded-lg shadow-sm"
          style={{ border: '1px solid #E5E7EB' }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center text-[#111827]">
              <Zap className="h-5 w-5 mr-2" style={{ stroke: '#D97706', fill: 'none' }} />
              Suggested Plays
            </CardTitle>
          </CardHeader>

          <CardContent className="pt-0">
            <Tabs defaultValue="do-more" className="w-full">
              {/* Tabs Header */}
              <TabsList
                className="
    flex w-full justify-between rounded-xl bg-[#F3F4F6] p-2 h-[55px]
   "
              >
                {[
                  { value: 'do-more', icon: TrendingUp, label: 'Do More' },
                  { value: 'change', icon: RefreshCw, label: 'Change/Stop' },
                  { value: 'start', icon: Zap, label: 'Start' },
                ].map(({ value, icon: Icon, label }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="
        flex items-center justify-center gap-2 w-full text-sm font-medium 
        text-[#4B5563] bg-transparent border-0 rounded-md transition-all
        data-[state=active]:bg-white data-[state=active]:text-[#111827]
        data-[state=active]:shadow-sm data-[state=active]:rounded-xl py-2.5
      "
                  >
                    <Icon className="h-4 w-4" />
                    {label} (
                    {
                      enrichedInsightData.plays.filter((play: any) => play.category === value)
                        .length
                    }
                    )
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Tabs Content */}
              {['do-more', 'change', 'start'].map((category) => (
                <TabsContent key={category} value={category} className="mt-6">
                  <div className="space-y-4">
                    {enrichedInsightData.plays.filter((play: any) => play.category === category)
                      .length > 0 ? (
                      enrichedInsightData.plays
                        .filter((play: any) => play.category === category)
                        .map((play: any) => (
                          <PlayCard
                            key={play.id}
                            play={play}
                            onAction={() => handlePlayAction(play.id)}
                            onBlock={handlePlayBlock}
                            onEditClick={handleOpenPlayEditModal}
                            getPriorityBadgeClass={getPriorityBadgeClass}
                            getImpactBadgeClass={getImpactBadgeClass}
                            isAccepted={play.suggestedStatus === 'ACCEPTED'}
                            isBlocked={play.suggestedStatus === 'REJECTED'}
                            currentUserId={currentUserId}
                          />
                        ))
                    ) : (
                      <div className="text-center py-8 text-[#6B7280]">
                        {category === 'do-more' && (
                          <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        )}
                        {category === 'change' && (
                          <RefreshCw className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        )}
                        {category === 'start' && (
                          <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        )}
                        <p>
                          No "
                          {category === 'do-more'
                            ? 'Do More'
                            : category === 'change'
                            ? 'Change/Stop'
                            : 'Start'}
                          " plays available for this insight.
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
        {/* Suggested Goals Section */}
        <Card
          className="mb-6 bg-white rounded-lg shadow-sm"
          style={{ border: '1px solid #E5E7EB' }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center text-[#111827]">
              <Target
                style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '8px',
                  stroke: '#3B82F6',
                  fill: 'none',
                }}
              />
              Suggested Goals
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-4">
              {enrichedInsightData.goals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onAction={() => handleGoalAction(goal.id)}
                  onBlock={handleGoalBlock}
                  getPriorityBadgeClass={getPriorityBadgeClass}
                  isAccepted={
                    goal.status !== UserTaskItemStatus.Pending &&
                    goal.status !== UserTaskItemStatus.Archived
                  }
                  isDeclined={goal.status === UserTaskItemStatus.Archived}
                  isLoading={loadingGoalId === goal.id}
                  isRejecting={rejectingGoalId === goal.id}
                />
              ))}
            </div>
          </CardContent>
        </Card>
        {/* Feedback Dialog - Conditionally render to ensure component unmounts and cache restoration works */}
        {feedbackDialogOpen && (
          <ErrorBoundary
            onReset={() => {
              setFeedbackDialogOpen(false)
              setBlockingItemTitle('')
              setBlockingPlayId(null)
              setBlockingPlayContext(null)
              setPlaySessionId(null)
              setFeedbackType('insight')
            }}
          >
            <FeedbackDialog
              isOpen={feedbackDialogOpen}
              onClose={() => {
                setFeedbackDialogOpen(false)
                setBlockingItemTitle('')
                setBlockingPlayId(null)
                setBlockingPlayContext(null)
                setPlaySessionId(null)
                setFeedbackType('insight')
              }}
              onSubmit={handleDetailedFeedbackSubmit}
              feedbackType={feedbackType}
              itemTitle={blockingItemTitle || enrichedInsightData?.title || ''}
              itemDescription={
                feedbackType === 'play'
                  ? blockingPlayContext?.playDescription
                  : enrichedInsightData?.description
              }
              itemId={feedbackType === 'play' ? blockingPlayId || undefined : insightId}
              existingSessionId={
                feedbackType === 'play'
                  ? playSessionId
                  : (insightResponse?.getInsight as any)?.session
              }
              onSessionCreated={handleFeedbackSessionCreated}
              insightContext={
                feedbackType === 'insight'
                  ? {
                      why: enrichedInsightData?.why,
                      priority: enrichedInsightData?.priority,
                      goals: enrichedInsightData?.goals?.map((goal: any) => ({
                        title: goal.title,
                        description: goal.description,
                        status: goal.status,
                      })),
                      plays: enrichedInsightData?.plays?.map((play: any) => ({
                        title: play.name,
                        description: play.description,
                        category: play.category,
                      })),
                    }
                  : undefined
              }
              playContext={feedbackType === 'play' ? blockingPlayContext || undefined : undefined}
            />
          </ErrorBoundary>
        )}
        {confirmModalOpen && (
          <AppModalBox
            title="Incomplete Goal Acceptance"
            onClose={() => {
              setConfirmModalOpen(false)
              setPendingSessionId(null)
              setPendingGoalId(null)
            }}
            width="w-[600px]"
          >
            <div className="flex flex-col gap-4 px-6 py-6">
              <p className="text-base text-app-grey-75 text-center">
                Do you want to complete accepting your previous goal or start a new one?
              </p>
              <div className="flex justify-end gap-3">
                <AppButton
                  variant="secondary"
                  size="lg"
                  label="Start New"
                  onClick={() => {
                    if (!pendingGoalId) return
                    createFlowChatSession(
                      {
                        input: {
                          flow: OnboardingFlowType.AcceptGoal,
                          metadata: JSON.stringify({
                            goalId: pendingGoalId,
                          }),
                        },
                      },
                      {
                        onSuccess: (res) => {
                          const { id } = res.createFlowChatSession
                          setAcceptGoalSessionId(id)
                          setIsAcceptGoalChatOpen(true)
                          setConfirmModalOpen(false)
                          setPendingSessionId(null)
                          setPendingGoalId(null)
                        },
                      }
                    )
                  }}
                />
                <AppButton
                  variant="primary"
                  size="lg"
                  label="Continue"
                  onClick={() => {
                    if (pendingSessionId) {
                      setAcceptGoalSessionId(pendingSessionId)
                      setIsAcceptGoalChatOpen(true)
                      setConfirmModalOpen(false)
                      setPendingSessionId(null)
                      setPendingGoalId(null)
                    }
                  }}
                />
              </div>
            </div>
          </AppModalBox>
        )}
        {/* Accept Goal Chat Popup */}
        {acceptGoalSessionId && (
          <AppAIChatPopup
            isOpen={isAcceptGoalChatOpen}
            onClose={() => {
              setIsAcceptGoalChatOpen(false)
              setAcceptGoalSessionId(null)
              // Refetch goals after closing to update status
              fetchGoalsList()
            }}
            sessionId={acceptGoalSessionId}
            refetchBusinessGoal={() => {
              // Refetch goals when goal is completed
              fetchGoalsList()
            }}
            title={
              <div className="flex items-center gap-3">
                <AppAvatar src={CoachLogicAvatar} size={'md'} type={'image'} shape={'Rounded'} />
                <div className="text-md font-medium">Coach Logic</div>
              </div>
            }
            width="w-[60vw]"
            userInitial={getInitials({
              firstName: user?.firstName || '',
              lastName: user?.lastName || '',
            })}
          />
        )}
        {/* Play Edit Modal */}
        {showPlayEditModal && selectedPlayForEdit && (
          <AppModalBox
            Icon={AppCrossIcon}
            height="max-h-[835px] h-full"
            width="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] xl:max-w-2xl mx-2 sm:mx-4 md:mx-auto"
            showBorder={false}
            title={`${
              selectedPlayForEdit.category ? `${categoryType[selectedPlayForEdit.category]} /` : ' '
            } ${selectedPlayForEdit.name || selectedPlayForEdit.title}`}
            titleSize="sm"
            onClose={() => {
              setShowPlayEditModal(false)
              setSelectedPlayForEdit(null)
              refetchPlays()
            }}
            onBack={() => {
              setShowPlayEditModal(false)
              setSelectedPlayForEdit(null)
              refetchPlays()
            }}
          >
            <AppTask
              task={selectedPlayForEdit}
              logo={logo ?? ''}
              tenantId={tenantId!}
              source={UserTaskSource.Goal}
              setShowTaskModal={setShowPlayEditModal}
              refetch={refetchPlays}
              userId={userId}
              userName={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
              advisorsData={advisorsData || []}
              plan={
                selectedPlayForEdit.goalId
                  ? (() => {
                      const goalData = goalsData.find(
                        (g) => g?.goalId === selectedPlayForEdit.goalId
                      )
                      return {
                        id: selectedPlayForEdit.goalId,
                        name: goalData?.name || 'Goal',
                        planExpectedEndDate: goalData?.expectedEndDate,
                      }
                    })()
                  : undefined
              }
              goalEndDate={
                selectedPlayForEdit.goalId
                  ? (() => {
                      const goalData = goalsData.find(
                        (g) => g?.goalId === selectedPlayForEdit.goalId
                      )
                      return goalData?.expectedEndDate || ''
                    })()
                  : ''
              }
              userAvatar={user?.avatar ?? ''}
              userFirstName={user?.firstName ?? ''}
              userLastName={user?.lastName ?? ''}
              goalId={selectedPlayForEdit.goalId ?? ''}
              assigneeOptions={assigneeOptions}
              paramUserId={selectedPlayForEdit.createdById || selectedPlayForEdit.userId || userId}
            />
          </AppModalBox>
        )}
      </div>
    </div>
  )
}

// Play Card Component
const PlayCard = ({
  play,
  onAction,
  onBlock,
  onEditClick,
  getPriorityBadgeClass,
  getImpactBadgeClass,
  isAccepted = false,
  isBlocked = false,
  currentUserId,
}: {
  play: Play
  onAction: () => void
  onBlock: (playId: string, playTitle: string) => void
  onEditClick?: (playId: string) => void
  getPriorityBadgeClass: (priority: string) => string
  getImpactBadgeClass: (impact: string) => string
  isAccepted?: boolean
  isBlocked?: boolean
  currentUserId: string
}) => {
  // Determine if we should show "Awaiting Acceptance"
  // This happens when:
  // 1. suggestedStatus is PENDING
  // 2. userId !== assigneeId (play has been reassigned)
  const showAwaitingAcceptance =
    play.suggestedStatus === 'PENDING' &&
    play.userId &&
    play.assigneeId &&
    play.userId !== play.assigneeId

  // Determine if we should show assignee information
  // Show assignee when:
  // 1. Play is reassigned (userId !== assigneeId) - for all states
  const showAssignee = play.userId && play.assigneeId && play.userId !== play.assigneeId

  // Determine if buttons should be disabled
  // Disable when it's awaiting acceptance OR when the current user is not the assignee
  const isDisabled =
    !!showAwaitingAcceptance || !!(play.assigneeId && play.assigneeId !== currentUserId)

  // Get avatar colors
  const coachLogicColor = { bg: '#FEF3C7', text: '#92400E' } // Fixed warm amber for Coach Logic
  const assigneeColor = play.assigneeId
    ? getAvatarColor(play.assigneeId)
    : { bg: '#E0E7FF', text: '#3730A3' }

  // Determine if play can be clicked to open modal
  // Allow modal to open when:
  // 1. Play has been reviewed (hasBeenViewed is true), OR
  // 2. Play is accepted, blocked, or awaiting acceptance
  const canOpenModal = play.hasBeenViewed || isAccepted || isBlocked || showAwaitingAcceptance

  // Handle card/title click to open edit modal
  const handleCardClick = () => {
    if (canOpenModal && onEditClick) {
      onEditClick(play.id)
    }
  }

  return (
    <div
      className={`bg-white rounded-lg p-6 shadow-sm transition-shadow duration-200 hover:shadow-lg ${
        canOpenModal ? 'cursor-pointer' : ''
      }`}
      style={{ border: '1px solid #E5E7EB' }}
      onClick={handleCardClick}
    >
      {/* Header with title */}
      <div className="mb-4">
        <h5 className="text-lg font-semibold text-gray-900 mb-3">{play.title}</h5>
        <p className="text-sm text-gray-600 leading-relaxed">{play.description}</p>
      </div>

      <div className="flex items-center mb-3">
        <div className="flex items-center gap-3">
          {/* Category badge */}
          <Badge
            variant="outline"
            className="bg-[#f3f4f6] text-[#374151] border-[#D1D5DB] rounded-full px-3 py-1 text-xs font-medium shadow-sm"
          >
            {(play as any).categoryDisplay || 'NULL'}
          </Badge>

          {/* play.priority */}
          <Badge
            variant="outline"
            className={`${getPriorityBadgeClass(
              play.priority
            )} rounded-full px-3 py-1 text-xs font-medium shadow-sm`}
          >
            {play.priority.charAt(0).toUpperCase() + play.priority.slice(1)} Priority
          </Badge>
        </div>
      </div>
      {/* Why It Matters section with lightbulb icon */}
      <div className="mb-4 p-3 bg-[#EFF6FF] border-l-4 border-[#60A5FA] rounded-r-md shadow-sm">
        <h6 className="text-sm font-semibold text-[#1E3A8A] mb-1">💡 Why It Matters</h6>
        <p className="text-sm text-[#1E40AF] leading-relaxed">{play.whyItMatters}</p>
      </div>

      {/* Bottom section */}
      <div className="flex items-center justify-between">
        {/* Left section - Suggested By / Assignee */}
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {/* Suggested By */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-lg">
            <span className="text-sm text-[#374151] font-medium">Suggested By</span>
            <Avatar
              style={{
                height: '1.5rem',
                width: '1.5rem',
              }}
            >
              <AvatarImage src="/images/coach-logic-avatar.png" alt="Coach Logic" />
              <AvatarFallback
                style={{
                  backgroundColor: coachLogicColor.bg,
                  color: coachLogicColor.text,
                  fontSize: '0.75rem',
                  fontWeight: '550',
                }}
              >
                CL
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Assignee - Show when play is reassigned */}
          {showAssignee && play.assigneeFirstName && play.assigneeLastName && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-lg">
              <span className="text-sm text-[#374151] font-medium">Assignee</span>
              <Avatar
                style={{
                  height: '1.5rem',
                  width: '1.5rem',
                }}
              >
                <AvatarFallback
                  style={{
                    backgroundColor: assigneeColor.bg,
                    color: assigneeColor.text,
                    fontSize: '0.75rem',
                    fontWeight: '550',
                  }}
                >
                  {play.assigneeFirstName.charAt(0)}
                  {play.assigneeLastName.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
        </div>

        {/* Right section - Awaiting Acceptance badge or Action buttons */}
        {showAwaitingAcceptance ? (
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="font-medium">Awaiting Acceptance</span>
          </div>
        ) : (
          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Block Button - Always visible */}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onBlock(play.id, play.title)
              }}
              disabled={isDisabled}
              className={`w-10 h-10 p-0 flex items-center justify-center rounded-lg border transition-all
              ${isBlocked ? 'bg-[#FEE2E2] border-[#FCA5A5]' : 'bg-white border-[#E5E7EB]'}
              ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50'}
            `}
              style={{
                boxShadow: 'none',
                background: isBlocked ? '#FEE2E2' : 'white',
                borderColor: isBlocked ? '#FCA5A5' : '#E5E7EB',
              }}
            >
              <X
                className={`h-5 w-5 ${isBlocked ? 'text-[#DC2626]' : 'text-[#9CA3AF]'}`}
                style={{
                  stroke: isBlocked ? '#DC2626' : '#9CA3AF',
                  fill: 'none',
                  strokeWidth: 2.5,
                }}
              />
            </Button>

            {/* Intercept Button - Always visible */}
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onAction()
              }}
              size="sm"
              disabled={isDisabled}
              className={`
            w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-all
            ${
              isAccepted
                ? 'bg-[#D1FAE5] border border-[#6EE7B7] hover:bg-[#A7F3D0]'
                : 'bg-white border border-[#E5E7EB] hover:border-[#6EE7B7]'
            }
            ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
          `}
            >
              <Check
                className={`h-5 w-5`}
                style={{
                  stroke: isAccepted ? '#059669' : '#9CA3AF',
                  fill: 'none',
                  strokeWidth: 2.5,
                }}
              />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

const GoalCard = ({
  goal,
  onAction,
  onBlock,
  getPriorityBadgeClass,
  isAccepted = false,
  isDeclined = false,
  isLoading = false,
  isRejecting = false,
}: {
  goal: Goal
  onAction: () => void
  onBlock: (goalId: string, goalTitle: string) => void
  getPriorityBadgeClass: (priority: string) => string
  isAccepted?: boolean
  isDeclined?: boolean
  isLoading?: boolean
  isRejecting?: boolean
}) => {
  // Get avatar color for Coach Logic
  const coachLogicColor = { bg: '#FEF3C7', text: '#92400E' } // Fixed warm amber for Coach Logic

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm transition-shadow duration-200 hover:shadow-lg border border-[#E5E7EB]">
      {/* Title & Description */}
      <h5 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>
        {goal.title}
      </h5>
      <p
        style={{ fontSize: '0.875rem', color: '#4B5563', marginBottom: '1rem', lineHeight: '1.5' }}
      >
        {goal.description}
      </p>

      <div className="flex items-center mb-3">
        <div className="flex items-center gap-3">
          {/* Category badge */}
          <Badge
            variant="outline"
            className="bg-[#f3f4f6] text-[#374151] border-[#D1D5DB] rounded-full px-3 py-1 text-xs font-medium shadow-sm"
          >
            {(goal as any).categoryDisplay || 'NULL'}
          </Badge>

          {/* goal.priority */}
          <Badge
            variant="outline"
            className={`${getPriorityBadgeClass(
              goal.priority
            )} rounded-full px-3 py-1 text-xs font-medium shadow-sm`}
          >
            {goal.priority.charAt(0).toUpperCase() + goal.priority.slice(1)} Priority
          </Badge>
        </div>
      </div>
      {/* Why It Matters */}
      <div
        style={{
          background: '#ECFDF5',
          borderLeft: '4px solid #34D399',
          // borderRadius: "0.375rem",
          padding: '0.75rem',
          marginBottom: '1rem',
        }}
      >
        <p
          style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#065F46',
            marginBottom: '0.25rem',
          }}
        >
          🎯 Why It Matters
        </p>
        <p style={{ fontSize: '0.875rem', color: '#047857', lineHeight: '1.5' }}>
          {goal.whyItMatters}
        </p>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: '0.75rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.75rem',
            color: '#4B5563',
            marginBottom: '0.25rem',
          }}
        >
          <span>Progress</span>
          <span>{goal.progress}% complete</span>
        </div>
        <Progress value={goal.progress} className="h-1" />
      </div>

      {/* Suggested by + Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.375rem 0.75rem',
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '0.5rem',
          }}
        >
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '400' }}>
            Suggested By
          </span>
          <Avatar
            style={{
              height: '1.5rem',
              width: '1.5rem',
            }}
          >
            <AvatarImage src="/images/coach-logic-avatar.png" alt="Coach Logic" />
            <AvatarFallback
              style={{
                backgroundColor: coachLogicColor.bg,
                color: coachLogicColor.text,
                fontSize: '0.75rem',
                fontWeight: '600',
              }}
            >
              CL
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {/* Block - Just X icon */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onBlock(goal.id, goal.title)}
            disabled={isDeclined || isLoading || isRejecting}
            className={`
            w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-all
            ${
              isDeclined
                ? 'bg-[#FEE2E2] border-[#FCA5A5]'
                : 'bg-white border border-[#E5E7EB] hover:border-[#FCA5A5]'
            }
          `}
          >
            {isRejecting ? (
              <Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF]" />
            ) : (
              <X
                className={`h-5 w-5`}
                style={{
                  stroke: isDeclined ? '#DC2626' : '#9CA3AF',
                  fill: 'none',
                  strokeWidth: 2.5,
                }}
              />
            )}
          </Button>

          {/* Intercept - Just Check icon */}
          <Button
            onClick={onAction}
            size="sm"
            disabled={isAccepted || isLoading}
            className={`
            w-10 h-10 p-0 flex items-center justify-center rounded-lg transition-all
            ${
              isAccepted
                ? 'bg-[#D1FAE5] border border-[#6EE7B7] hover:bg-[#A7F3D0]'
                : 'bg-white border border-[#E5E7EB] hover:border-[#6EE7B7]'
            }
          `}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF]" />
            ) : (
              <Check
                className={`h-5 w-5`}
                style={{
                  stroke: isAccepted ? '#059669' : '#9CA3AF',
                  fill: 'none',
                  strokeWidth: 2.5,
                }}
              />
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default InsightDetail
